import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { GeofenceStyleFormModel } from '@casops/side-panels/manage-geofence-style/useManageGeofenceStyle';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import Field from '@cas/components/inputs/field/Field';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import ColourSelector from '../../colour-selector/ColourSelector';
import styles from './FormColourConfigSelector.module.scss';
import ColourButton from '../../colour-button/ColourButton';
import solidImageUrl from '@cas/assets/patterns/solid.png';
import dense1ImageUrl from '@cas/assets/patterns/dense1.png';
import dense2ImageUrl from '@cas/assets/patterns/dense2.png';
import dense3ImageUrl from '@cas/assets/patterns/dense3.png';
import dense4ImageUrl from '@cas/assets/patterns/dense4.png';
import dense5ImageUrl from '@cas/assets/patterns/dense5.png';
import dense6ImageUrl from '@cas/assets/patterns/dense6.png';
import dense7ImageUrl from '@cas/assets/patterns/dense7.png';
import verticalImageUrl from '@cas/assets/patterns/vertical.png';
import crossImageUrl from '@cas/assets/patterns/cross.png';
import diagCrossImageUrl from '@cas/assets/patterns/diagcross.png';

const colourFillPatternOptionsData = [
  { key: 'NoBrush', label: 'No Brush', value: 0, imageUrl: '' },
  { key: 'Solid', label: 'Solid', value: 1, imageUrl: solidImageUrl },
  { key: 'Dense1', label: 'Dense 1', value: 2, imageUrl: dense1ImageUrl },
  { key: 'Dense2', label: 'Dense 2', value: 3, imageUrl: dense2ImageUrl },
  { key: 'Dense3', label: 'Dense 3', value: 4, imageUrl: dense3ImageUrl },
  { key: 'Dense4', label: 'Dense 4', value: 5, imageUrl: dense4ImageUrl },
  { key: 'Dense5', label: 'Dense 5', value: 6, imageUrl: dense5ImageUrl },
  { key: 'Dense6', label: 'Dense 6', value: 7, imageUrl: dense6ImageUrl },
  { key: 'Dense7', label: 'Dense 7', value: 8, imageUrl: dense7ImageUrl },
  { key: 'Vertical', label: 'Vertical', value: 10, imageUrl: verticalImageUrl },
  { key: 'Crossing', label: 'Crossing', value: 11, imageUrl: crossImageUrl },
  { key: 'DiagCross', label: 'Diag Cross', value: 14, imageUrl: diagCrossImageUrl },
] as const;

const colourOpacityOptionsData = [
  { label: '0', value: 0 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '60', value: 60 },
  { label: '70', value: 70 },
  { label: '80', value: 80 },
  { label: '90', value: 90 },
  { label: '100', value: 100 },
] as const;

const opacityDropdownItems: DropdownItemProps<ColourOpacityValue>[] = colourOpacityOptionsData.map(
  ({ label, value }) => ({
    key: label,
    value,
    stringValue: label,
    label,
  }),
);

export type ColourFillPatternKey = (typeof colourFillPatternOptionsData)[number]['key'];
export type ColourFillPatternValue = (typeof colourFillPatternOptionsData)[number]['value'];
export type ColourOpacityValue = (typeof colourOpacityOptionsData)[number]['value'];

const FILL_DEFAULTCOLOUR = 'fill.defaultColour';
const FILL_ACTIVECOLOUR = 'fill.activeColour';
const FILL_PATTERN = 'fill.pattern';
const FILL_OPACITY = 'fill.opacity';

const FormColourConfigSelector = ({
  control,
  watch,
  setValue,
}: {
  control: UseFormReturn<GeofenceStyleFormModel>['control'];
  watch: UseFormReturn<GeofenceStyleFormModel>['watch'];
  setValue: UseFormReturn<GeofenceStyleFormModel>['setValue'];
}) => {
  const { t } = useTranslation();
  const watchFillActiveColour = watch(FILL_ACTIVECOLOUR);
  const watchFillDefaultColour = watch(FILL_DEFAULTCOLOUR);
  const watchFillPattern = watch(FILL_PATTERN);
  const watchFillOpacity = watch(FILL_OPACITY);

  const patternImageUrl = useMemo(() => {
    const pattern = colourFillPatternOptionsData.find(p => p.value === watchFillPattern);
    return pattern?.imageUrl ?? '';
  }, [watchFillPattern]);

  const fillPatternDropdownItems = useMemo(
    () =>
      colourFillPatternOptionsData.map(({ key, label, value }) => ({
        key: label,
        value,
        stringValue: label,
        label: t(`FillPatterns.${key}`),
      })),
    [t],
  );

  const colourSelectorHandler = useCallback(
    (name: typeof FILL_DEFAULTCOLOUR | typeof FILL_ACTIVECOLOUR, colour: string) => {
      setValue(name, colour, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  const renderColourSection = (
    name: typeof FILL_DEFAULTCOLOUR | typeof FILL_ACTIVECOLOUR,
    colour: string,
  ) => {
    const fieldName = name.split('.')[1];
    return (
      <div className={styles.colourPreviewSection}>
        <div className={styles.colourInput}>
          <CasFormInput control={control} name={name} label={t(`Fields.${fieldName}`)} />
        </div>
        <div className={styles.colourSelector}>
          <ColourSelector
            colour={colour}
            label={t('Fields.colourSelector')}
            onChange={c => colourSelectorHandler(name, c)}
          />
        </div>
        <div className={styles.preview}>
          <Field uid={`${name}-preview`} label={t('Fields.preview')}>
            <ColourButton
              colour={colour}
              patternImageUrl={patternImageUrl}
              makeTransparent={!patternImageUrl}
              opacity={watchFillOpacity}
            />
          </Field>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.section}>
      <h3 className={styles.heading}>{t('FillColour')}</h3>
      {renderColourSection(FILL_DEFAULTCOLOUR, watchFillDefaultColour)}
      {renderColourSection(FILL_ACTIVECOLOUR, watchFillActiveColour)}
      <div className={styles.colourOptionsSection}>
        <div className={styles.fillPattern}>
          <CasFormDropdown
            control={control}
            name="fill.pattern"
            label={t('Fields.FillPattern')}
            items={fillPatternDropdownItems}
          />
        </div>
        <div className={styles.fillOpacity}>
          <CasFormDropdown
            control={control}
            name="fill.opacity"
            label={t('Fields.Opacity')}
            items={opacityDropdownItems}
          />
        </div>
      </div>
    </div>
  );
};

export default FormColourConfigSelector;
